/**
 * channelList.tsx
 */
import ArrowRight from '@components/atoms/icons/arrowRight';
import ArrowRightContainer from '@components/atoms/container/channelListArrowRightContainer';
import ChannelItem from '@components/elements/channelList/channelListChannelItem';
import ChannelUtils from '@shared/utils/channels';
import Colors from '@shared/assets/style/default/color';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import List from '../list';
import ListContainer from '@components/atoms/container/channelListContainer';
import ManageChannelContext from '@shared/components/utils/context/manageChannelsContext';
import Pages from '@shared/types/enums/pages';
import closeMenu from '@shared/actions/menu/closeMenu';
import goToChannel from '@shared/actions/channels/goToChannel';
import useTransform from '@shared/components/utils/hooks/useTransform';
import { ChannelListProps as Props } from '@shared/types/props/channelList';
import { selectRoute } from '@shared/store/slices/router.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import {
  focusSelector,
  popFocus,
  pushFocus,
  setFocus,
} from '@shared/store/slices/focus.slice';

/**
 * Constants
 */
const ITEM_OFFSET = 104;
const ITEMS_TO_ATTACH = 7;

/**
 * ChannelList component.
 */
export default function ChannelList({
  circular,
  channelsData,
  currentIndex,
  focused,
  preventAction,
  onChangeState,
  outOfBounds = () => null,
}: Props) {
  const dispatch = useAppDispatch();
  const { focus, stack } = useAppSelector(focusSelector);
  const route = useAppSelector(selectRoute);
  const { editing } = useContext(ManageChannelContext);

  // Memoized data

  const { data, initialIndex } = useMemo(() => {
    // Channels data
    const available = circular
      ? [
          ...channelsData.slice(-ITEMS_TO_ATTACH),
          ...channelsData,
          ...channelsData.slice(0, ITEMS_TO_ATTACH),
        ]
      : channelsData;
    const unavailable = ChannelUtils.getUnavailableChannels(channelsData);
    const initialIndex = circular
      ? available.indexOf(channelsData[currentIndex], ITEMS_TO_ATTACH)
      : currentIndex;

    return { data: { available, unavailable }, initialIndex };
  }, [channelsData]);

  const { delay, displayArrow } = useMemo(() => {
    const delay = focused ? 200 : 0;
    const displayArrow =
      !editing &&
      (focus === Focus.CHANNEL_LIST || stack.includes(Focus.CHANNEL_LIST));

    return { delay, displayArrow };
  }, [editing, focus, focused, stack]);

  // References

  const channelsDataRef = useRef(data.available);
  if (!circular) {
    channelsDataRef.current = channelsData;
  }

  // Actions

  const onChange = (index: number, increment = 1) => {
    setStyle(increment);
    onChangeState && onChangeState(channelsDataRef.current[index], increment);
    if (circular) {
      const lastItemIndex =
        channelsDataRef.current.length - ITEMS_TO_ATTACH - 1;
      if (index < ITEMS_TO_ATTACH || index > lastItemIndex) {
        dispatch(pushFocus(-1));
        setTimeout(() => {
          const newIndex =
            increment > 0
              ? ITEMS_TO_ATTACH + (index - lastItemIndex - 1)
              : lastItemIndex - (ITEMS_TO_ATTACH - index - 1);
          setListIndex.current(newIndex, 0);
          onChangeState &&
            onChangeState(channelsDataRef.current[newIndex], increment);
          dispatch(popFocus());
        }, 150);
      }
    }
  };

  const setListIndex = useRef<(newIndex: number, increment?: number) => void>(
    () => null
  );

  // Effect hooks

  useEffect(() => {
    if (setListIndex.current) {
      setListIndex.current(initialIndex, 0);
    }
  }, [initialIndex]);

  // Transform hook

  const [style, setStyle] = useTransform(
    initialIndex,
    ITEM_OFFSET,
    Direction.VERTICAL
  );

  return data.available ? (
    <>
      <ListContainer
        style={style}
        onMouseEnter={() => {
          closeMenu();
          dispatch(setFocus(Focus.CHANNEL_LIST));
        }}
        id={'listContainer'}
      >
        <List
          direction={Direction.VERTICAL}
          focus={Focus.CHANNEL_LIST}
          initialIndex={initialIndex}
          numberOfPressures={2}
          onChange={onChange}
          outOfBounds={outOfBounds}
          setListIndex={setListIndex}
        >
          {channelsDataRef.current.map((item, index) => {
            const available = !data.unavailable.includes(item);
            return (
              <ChannelItem
                {...item}
                available={available}
                index={index}
                key={index}
                releaseAction={() => {
                  if (!preventAction) {
                    goToChannel({
                      channel: item,
                      checkParental: true,
                      pop: !!route.stack.length,
                    });
                  }
                }}
              />
            );
          })}
        </List>
      </ListContainer>
      {displayArrow ? (
        <ArrowRightContainer
          delay={delay}
          hasDial={route.Page === Pages.CHANNELS_LINEUP}
          id={'arrowRightContainer'}
        >
          <ArrowRight color={Colors.selector} height={33} width={20} />
        </ArrowRightContainer>
      ) : null}
    </>
  ) : null;
}
